import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { getAvatarUrl } from '@/lib/avatars';

interface FollowerProfile {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface FollowerAvatarsProps {
  followers: FollowerProfile[];
  /** Maximum avatars to show before "+X" */
  maxVisible?: number;
  /** Size of avatars */
  size?: 'sm' | 'md' | 'lg';
  /** Additional class names */
  className?: string;
  /** Whether to show on dark background (overlay style) */
  overlay?: boolean;
  /** Click handler - makes the entire component tappable */
  onClick?: () => void;
}

const sizeClasses = {
  sm: 'w-5 h-5 text-[8px]',
  md: 'w-6 h-6 text-[10px]',
  lg: 'w-8 h-8 text-xs',
};

const overlapClasses = {
  sm: '-ml-1.5 first:ml-0',
  md: '-ml-2 first:ml-0',
  lg: '-ml-2.5 first:ml-0',
};

export function FollowerAvatars({ 
  followers, 
  maxVisible = 3, 
  size = 'sm',
  className,
  overlay = false,
  onClick,
}: FollowerAvatarsProps) {
  if (followers.length === 0) return null;

  const visibleFollowers = followers.slice(0, maxVisible);
  const remainingCount = followers.length - maxVisible;

  const content = (
    <>
      {visibleFollowers.map((follower, index) => (
        <Avatar 
          key={follower.user_id} 
          className={cn(
            sizeClasses[size],
            overlapClasses[size],
            overlay 
              ? "border-2 border-white/50 ring-0" 
              : "border-2 border-background ring-0",
            "relative"
          )}
          style={{ zIndex: maxVisible - index }}
        >
          <AvatarImage 
            src={getAvatarUrl(follower.avatar_url, follower.user_id)} 
            alt={follower.display_name || 'Follower'} 
          />
          <AvatarFallback 
            className={cn(
              "bg-muted",
              sizeClasses[size]
            )}
          />
        </Avatar>
      ))}
      
      {remainingCount > 0 && (
        <div 
          className={cn(
            sizeClasses[size],
            overlapClasses[size],
            "rounded-full flex items-center justify-center font-medium",
            overlay 
              ? "bg-white/20 text-white border-2 border-white/50 backdrop-blur-sm" 
              : "bg-muted text-muted-foreground border-2 border-background"
          )}
          style={{ zIndex: 0 }}
        >
          +{remainingCount}
        </div>
      )}
    </>
  );

  if (onClick) {
    return (
      <button 
        onClick={onClick}
        className={cn(
          "flex items-center rounded-full transition-transform active:scale-95",
          className
        )}
        aria-label={`View ${followers.length} collaborators`}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={cn("flex items-center", className)}>
      {content}
    </div>
  );
}
